import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ArrowLeft, Search, ChevronRight, ChevronUp, ChevronDown,
  Plus, Trash2, GripVertical, Save, Loader2, Info,
} from "lucide-react";
import { MediaPicker } from "@/components/media/MediaPicker";
import { AttachPackagesCard } from "@/components/catalog/AttachPackagesCard";
import { useMenuItems } from "@/hooks/useMenuItems";
import { useCategories } from "@/hooks/useCategories";
import {
  usePublishedMenu,
  useCreatePublishedMenu,
  useUpdatePublishedMenu,
} from "@/hooks/usePublishedMenus";
import { useDietaryTags, getDietaryTagLabel } from "@/hooks/useDietaryTags";

/* ─── Local draft types ─── */

interface SectionDraft {
  id: string;
  title: string;
  description: string;
  sort_order: number;
  items: SectionItemDraft[];
}

interface SectionItemDraft {
  menu_item_id: string;
  sort_order: number;
  price_override?: number;
  description_override?: string;
}

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

/** Tap/click/keyboard help for a setting. A hover tooltip never opens on touch (#694). */
function HelpHint({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${label}`}
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="text-sm">{children}</PopoverContent>
    </Popover>
  );
}

/* ─── Component ─── */

export default function MenuBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: editingMenu, isLoading: menuLoading, refetch: refetchMenu, isFetching: menuRefetching } = usePublishedMenu(id);
  const { data: menuItems } = useMenuItems();
  const { data: categories } = useCategories();
  const { data: dietaryTags } = useDietaryTags();
  const createMenu = useCreatePublishedMenu();
  const updateMenu = useUpdatePublishedMenu();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [description, setDescription] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [showPrices, setShowPrices] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [isListed, setIsListed] = useState(false);
  const [ctaText, setCtaText] = useState("Request a Quote");
  const [sections, setSections] = useState<SectionDraft[]>([]);
  const [packageIds, setPackageIds] = useState<string[]>([]);
  const [itemSearch, setItemSearch] = useState("");
  const [dirty, setDirty] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (isEdit && editingMenu && !initialized) {
      setTitle(editingMenu.title);
      setSlug(editingMenu.slug);
      setSlugManual(true);
      setDescription(editingMenu.description || "");
      setCoverImageUrl(editingMenu.cover_image_url || "");
      setShowPrices(editingMenu.show_prices);
      setIsPublished(editingMenu.is_published);
      setIsListed(!!editingMenu.is_listed);
      setCtaText(editingMenu.cta_text);
      setPackageIds(editingMenu.package_ids);
      setSections(
        (editingMenu.sections || []).map((s, i) => ({
          id: crypto.randomUUID(),
          title: s.title,
          description: s.description || "",
          sort_order: i,
          items: (s.items || []).map((item, j: number) => ({
            menu_item_id: item.menu_item_id,
            sort_order: j,
            price_override: item.price_override ?? undefined,
            description_override: item.description_override ?? undefined,
          })),
        }))
      );
      setInitialized(true);
    }
  }, [isEdit, editingMenu, initialized]);

  // Mark as initialized for create mode
  useEffect(() => {
    if (!isEdit) setInitialized(true);
  }, [isEdit]);

  // Auto-slug from title
  useEffect(() => {
    if (!slugManual && title) setSlug(slugify(title));
  }, [title, slugManual]);

  // Track dirty state
  useEffect(() => {
    if (initialized) setDirty(true);
    // initialized is a one-way latch set after populating form state; adding it
    // here would cause the dirty flag to flip the moment the form hydrates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, slug, description, coverImageUrl, showPrices, isPublished, isListed, ctaText, sections, packageIds]);

  // Warn before navigating away with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  /* ─── Section helpers ─── */

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      { id: crypto.randomUUID(), title: "", description: "", sort_order: prev.length, items: [] },
    ]);
  };

  const updateSection = useCallback((idx: number, patch: Partial<SectionDraft>) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }, []);

  const removeSection = (idx: number) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveSection = (idx: number, dir: -1 | 1) => {
    setSections((prev) => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const toggleItemInSection = (sectionIdx: number, menuItemId: string) => {
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== sectionIdx) return s;
        const exists = s.items.find((it) => it.menu_item_id === menuItemId);
        if (exists) return { ...s, items: s.items.filter((it) => it.menu_item_id !== menuItemId) };
        return { ...s, items: [...s.items, { menu_item_id: menuItemId, sort_order: s.items.length }] };
      })
    );
  };

  /* ─── Grouped items for picker ─── */

  const grouped = useMemo(() => {
    if (!menuItems || !categories) return [];
    const search = itemSearch.toLowerCase();
    const filtered = menuItems.filter(
      (mi) =>
        mi.is_active &&
        (mi.name.toLowerCase().includes(search) || mi.category?.name?.toLowerCase().includes(search))
    );
    const cats = categories.filter((c) => c.is_active).sort((a, b) => a.sort_order - b.sort_order);
    return cats
      .map((cat) => ({ ...cat, items: filtered.filter((mi) => mi.category_id === cat.id) }))
      .filter((g) => g.items.length > 0);
  }, [menuItems, categories, itemSearch]);

  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);

  /* ─── Submit ─── */

  const handleSubmit = () => {
    if (!title.trim() || !slug.trim()) return;
    // Edit mode must never save before the menu hydrated into local state.
    if (isEdit && !initialized) return;
    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      description: description.trim() || undefined,
      cover_image_url: coverImageUrl.trim() || undefined,
      show_prices: showPrices,
      is_published: isPublished,
      is_listed: isListed && isPublished,
      cta_text: ctaText.trim() || "Request a Quote",
      package_ids: packageIds,
      sections: sections.map((s, i) => ({
        title: s.title.trim() || `Section ${i + 1}`,
        description: s.description.trim() || undefined,
        sort_order: i,
        items: s.items.map((it, j) => ({
          menu_item_id: it.menu_item_id,
          sort_order: j,
          price_override: it.price_override,
          description_override: it.description_override?.trim() || undefined,
        })),
      })),
    };

    const opts = { onSuccess: () => { setDirty(false); navigate("/admin/catalog?tab=menus"); } };

    if (isEdit && id) {
      updateMenu.mutate({ id, ...payload }, opts);
    } else {
      createMenu.mutate(payload, opts);
    }
  };

  const busy = createMenu.isPending || updateMenu.isPending;

  if (isEdit && menuLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  // Never show a blank, saveable form for a menu that failed to load: saving it
  // would replace the menu's sections and packages with nothing (#932).
  // Keyed on missing data, not `isError`: a failed background refetch keeps the
  // last good data, so the form (and the user's unsaved edits) stays usable.
  if (isEdit && !editingMenu) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center space-y-3">
          <p className="text-sm text-muted-foreground">Couldn't load this menu.</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/catalog?tab=menus")}>Back to menus</Button>
            <Button size="sm" onClick={() => void refetchMenu()} disabled={menuRefetching}>
              {menuRefetching ? "Trying…" : "Try again"}
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Sticky header */}
      <div className="-m-6 mb-0">
        {/* Wraps onto two lines when the actions don't fit beside the title (phones, tablets with the sidebar open). */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 sm:px-6 py-3 border-b bg-card sticky top-0 z-10">
          <div className="flex flex-1 min-w-[12rem] items-center gap-3">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => navigate("/admin/catalog?tab=menus")} aria-label="Back to menus">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-display text-lg font-semibold text-foreground">
                {isEdit ? "Edit Menu" : "New Menu"}
              </h1>
              {title && <p className="truncate text-xs text-muted-foreground">{title}</p>}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Published</Label>
              <Switch checked={isPublished} onCheckedChange={setIsPublished} />
            </div>
            {/* The back arrow does the same thing, so phones drop Cancel to keep Save on screen. */}
            <Button variant="outline" className="hidden sm:inline-flex" onClick={() => navigate("/admin/catalog?tab=menus")}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={busy || !title.trim() || !slug.trim()}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {busy ? "Saving..." : isEdit ? "Save Changes" : "Create Menu"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
          {/* LEFT: Meta — 2 cols */}
          <div className="lg:col-span-2 space-y-5 min-w-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Menu Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Wedding Menu" />
                </div>
                <div>
                  <Label>Slug *</Label>
                  <Input
                    value={slug}
                    onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
                    placeholder="wedding-menu"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Public URL: /menu/{slug || "..."}</p>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="A brief intro for this menu..." />
                </div>
                <div>
                  <Label>Cover Image</Label>
                  <MediaPicker
                    value={coverImageUrl}
                    onChange={setCoverImageUrl}
                    folder="menus/"
                    buttonLabel="Browse"
                  />
                </div>
                <div>
                  <Label>CTA Button Text</Label>
                  <Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Request a Quote" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Visibility</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <Label>Show prices</Label>
                    <HelpHint label="Show prices">
                      Display item prices on the public menu. Turn off to share menus without pricing.
                    </HelpHint>
                  </div>
                  <Switch checked={showPrices} onCheckedChange={setShowPrices} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <Label>Accessible by direct link</Label>
                    <HelpHint label="Accessible by direct link">
                      Anyone with the link can view this menu at tristanoscatering.ca/menu/{slug || "<slug>"}. Turn off to keep it internal.
                    </HelpHint>
                  </div>
                  <Switch
                    checked={isPublished}
                    onCheckedChange={(v) => {
                      setIsPublished(v);
                      if (!v) setIsListed(false);
                    }}
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <Label className={!isPublished ? "text-muted-foreground" : ""}>Listed on website</Label>
                    <HelpHint label="Listed on website">
                      {isPublished
                        ? "Show this menu on the public /menus page so visitors can discover it."
                        : "Enable \"Accessible by direct link\" first — a listed menu must be openable."}
                    </HelpHint>
                  </div>
                  <Switch
                    checked={isListed && isPublished}
                    disabled={!isPublished}
                    onCheckedChange={setIsListed}
                  />
                </div>
              </CardContent>
            </Card>

            <AttachPackagesCard packageIds={packageIds} onChange={setPackageIds} />
          </div>

          {/* RIGHT: Sections — 3 cols */}
          <div className="lg:col-span-3 space-y-4 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-foreground">
                Sections{" "}
                <Badge variant="secondary" className="ml-1">{totalItems} items</Badge>
              </h2>
              <Button size="sm" variant="outline" onClick={addSection}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Section
              </Button>
            </div>

            {sections.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    Add a section to start building your menu.
                  </p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={addSection}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add First Section
                  </Button>
                </CardContent>
              </Card>
            )}

            {sections.map((section, sIdx) => (
              <Card key={section.id}>
                <CardContent className="pt-4 space-y-3">
                  {/* Section header */}
                  <div className="flex items-center gap-2">
                    <GripVertical className="hidden sm:block h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                      placeholder="Section name (e.g. Appetizers)"
                      className="min-w-0"
                    />
                    <div className="flex shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} aria-label="Move section up">
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sections.length - 1} aria-label="Move section down">
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeSection(sIdx)} aria-label="Remove section">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Input
                    value={section.description}
                    onChange={(e) => updateSection(sIdx, { description: e.target.value })}
                    placeholder="Section description (optional)"
                    className="text-base md:text-xs"
                  />

                  {/* Item picker */}
                  <div className="border-t border-border pt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={itemSearch}
                        onChange={(e) => setItemSearch(e.target.value)}
                        placeholder="Search items..."
                        className="pl-9"
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {grouped.map((cat) => (
                        <Collapsible key={cat.id} defaultOpen>
                          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full py-1.5 hover:text-foreground">
                            <ChevronRight className="h-3 w-3 transition-transform data-[state=open]:rotate-90" />
                            {cat.name}
                            <span className="ml-auto text-[10px]">{cat.items.length}</span>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="pl-5 space-y-0.5">
                            {cat.items.map((mi) => {
                              const selected = section.items.some((it) => it.menu_item_id === mi.id);
                              return (
                                <label key={mi.id} className="flex items-start gap-2 py-2 sm:py-1 text-sm cursor-pointer hover:bg-muted/50 rounded px-1.5">
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() => toggleItemInSection(sIdx, mi.id)}
                                    className="h-4 w-4 mt-0.5 shrink-0"
                                  />
                                  {/* Badges wrap under a long name rather than widening the page (#694). */}
                                  <span className="flex flex-1 min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className={selected ? "font-medium" : ""}>{mi.name}</span>
                                    {(mi.dietary_tags || []).map((tag: string) => (
                                      <Badge key={tag} variant="outline" className="text-[10px] px-1 py-0 h-4">
                                        {getDietaryTagLabel(dietaryTags, tag)}
                                      </Badge>
                                    ))}
                                  </span>
                                </label>
                              );
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </div>
                  </div>

                  {/* Selected items summary */}
                  {section.items.length > 0 && (
                    <div className="text-xs text-muted-foreground pt-1 border-t border-border">
                      {section.items.length} item{section.items.length !== 1 ? "s" : ""} selected
                    </div>
                  )}
                </CardContent>
              </Card>
          ))}
          </div>
        </div>
    </DashboardLayout>
  );
}
